import React, { useCallback, useMemo } from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import {
  SearchField,
  SearchFieldProps,
} from '@lifeomic/chroma-react/components/SearchField';
import { useSearchContext } from './providers/Search';
import {
  useLabResultsConfig,
  LabResultsConfigItem,
} from './useLabResultsConfig';
import { useFetchDimensionData } from './hooks/useFetchDimensionData';
import { useFetchMedicalResultHistories } from './hooks/useFetchMedicalResultHistories';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { filterMedicalResultHistories, RECENCY_OPTIONS } from './utils';
import { useSelectionContext } from './providers/Selection';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { defineMessages, useIntl } from 'react-intl';
import { ChevronDown } from '@lifeomic/chromicons';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { RadioGroup, Radio } from '@lifeomic/chroma-react/components/Radio';

const messages = defineMessages({
  all: {
    id: 'labResults.sidebar.recency.all',
    defaultMessage: 'All Time',
  },
  last_30_days: {
    id: 'labResults.sidebar.recency.last_30_days',
    defaultMessage: 'Last 30 days',
  },
  last_60_days: {
    id: 'labResults.sidebar.recency.last_60_days',
    defaultMessage: 'Last 60 days',
  },
  last_90_days: {
    id: 'labResults.sidebar.recency.last_90_days',
    defaultMessage: 'Last 90 days',
  },
  last_180_days: {
    id: 'labResults.sidebar.recency.last_180_days',
    defaultMessage: 'Last 180 days',
  },
  withResultsIn: {
    id: 'labResults.sidebar.recency.withResultsIn',
    defaultMessage: 'With results in...',
  },
  titleWithCount: {
    id: 'labResults.sidebar.titleWithCount',
    defaultMessage: '{title} ({count})',
  },
  resultsType: {
    id: 'labResults.sidebar.resultsType',
    defaultMessage: 'Results Type',
  },
  resultsTypeAll: {
    id: 'labResults.sidebar.resultsType.all',
    defaultMessage: 'All',
  },
  resultsTypePersonalized: {
    id: 'labResults.sidebar.resultsType.personalized',
    defaultMessage: 'Personalized',
  },
  labSource: {
    id: 'labResults.sidebar.labSource',
    defaultMessage: 'Lab Source',
  },
  labSourceAll: {
    id: 'labResults.sidebar.labSource.all',
    defaultMessage: 'All labs',
  },
  labSourceExtracted: {
    id: 'labResults.sidebar.labSource.extracted',
    defaultMessage: 'Extracted labs',
  },
});

const useStyles = makeStyles(theme => ({
  button: {
    textAlign: 'left',
    padding: theme.spacing(0.5, 1.25),
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.palette.action.hover,
      '& $navButtonText': {
        color:
          theme.palette.mode === 'dark'
            ? theme.palette.blue[200]
            : theme.palette.blue.main,
      },
    },
  },
  navButtonText: {
    color: theme.palette.text.primary,
  },
  navButtonTextDisabled: {
    color: theme.palette.text.secondary,
  },
  recencyButton: {
    color: theme.palette.text.primary,
    padding: 0,
    minHeight: 'auto',
    '&:hover:not(:disabled)': {
      color: theme.palette.blue[200],
      backgroundColor: 'transparent',
    },
  },
}));

type SidebarProps = {
  patient: fhir.Patient;
  className?: string;
};

export function Sidebar({ patient, className }: SidebarProps) {
  return (
    <Box className={className} direction="column" padding={1.5}>
      <SearchInput />
      <Divider />
      <RecencyMenu />
      <Divider />
      <LabSourceFilter />
      <Divider />
      <Body patient={patient} />
      <Divider />
      <ResultsTypeFilter />
    </Box>
  );
}

type BodyProps = {
  patient: fhir.Patient;
};

function Body({ patient }: BodyProps) {
  const labResultsConfig = useLabResultsConfig();

  return (
    <Box direction="column" paddingTop={1}>
      {labResultsConfig.map(item => (
        <BodySection
          key={item.title}
          labResultsConfigItem={item}
          patient={patient}
        />
      ))}
    </Box>
  );
}

type BodySectionProps = {
  labResultsConfigItem: LabResultsConfigItem;
  patient: fhir.Patient;
};

function BodySection({ labResultsConfigItem, patient }: BodySectionProps) {
  const classes = useStyles();
  const { debouncedSearchTerm, recency, annotatedOnly, extractedOnly } =
    useSearchContext();
  const { setSelection } = useSelectionContext();
  const { formatMessage } = useIntl();

  const { data: dimensionData } = useFetchDimensionData({
    patientId: patient.id as string,
    labResultsConfigItem,
  });

  const { data: medicalResultHistories } = useFetchMedicalResultHistories({
    patientId: patient.id as string,
    dimensionData,
    labResultsConfigItem,
  });

  const filteredMedicalResultHistories = useMemo(() => {
    if (medicalResultHistories === undefined) {
      return [];
    }
    return filterMedicalResultHistories(
      medicalResultHistories,
      debouncedSearchTerm,
      recency,
      annotatedOnly,
      extractedOnly,
    );
  }, [
    medicalResultHistories,
    debouncedSearchTerm,
    recency,
    annotatedOnly,
    extractedOnly,
  ]);

  const handleLabResultsSectionClick = useCallback(() => {
    setSelection(labResultsConfigItem);
  }, [labResultsConfigItem, setSelection]);

  const hasResults = filteredMedicalResultHistories.length > 0;

  return (
    <ButtonUnstyled
      className={classes.button}
      onClick={handleLabResultsSectionClick}
      disabled={!hasResults}
    >
      <Text
        size="subbody"
        className={
          hasResults ? classes.navButtonText : classes.navButtonTextDisabled
        }
      >
        {formatMessage(messages.titleWithCount, {
          title: labResultsConfigItem.title,
          count: filteredMedicalResultHistories.length,
        })}
      </Text>
    </ButtonUnstyled>
  );
}

function SearchInput(props: SearchFieldProps) {
  const { searchTerm, setSearchTerm } = useSearchContext();

  return (
    <SearchField
      fullWidth
      placeholder="Search lab results..."
      value={searchTerm}
      onChange={e => setSearchTerm(e.target.value)}
      aria-label="Search lab results"
      {...props}
    />
  );
}

const useMenuStyles = makeStyles(theme => ({
  label: {
    marginTop: theme.spacing(1),
    ...theme.typography.caption,
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

function RecencyMenu() {
  const { recency, setRecency } = useSearchContext();
  const { formatMessage } = useIntl();

  const classes = useMenuStyles();
  const buttonClasses = useStyles();

  return (
    <Menu
      aria-label="Recency"
      anchorElement={
        <label className={classes.label}>
          <span>{formatMessage(messages.withResultsIn)}</span>
          <Button
            variant="text"
            trailingIcon={ChevronDown}
            className={buttonClasses.recencyButton}
          >
            {formatMessage(messages[recency as keyof typeof messages])}
          </Button>
        </label>
      }
      items={RECENCY_OPTIONS.map(option => (
        <MenuItem
          key={option}
          value={option}
          onClick={() => setRecency(option)}
        >
          {formatMessage(messages[option])}
        </MenuItem>
      ))}
    ></Menu>
  );
}

function ResultsTypeFilter() {
  const { annotatedOnly, setAnnotatedOnly } = useSearchContext();
  const { formatMessage } = useIntl();

  return (
    <Box paddingTop={1}>
      <RadioGroup
        title={formatMessage(messages.resultsType)}
        name="resultsType"
        value={annotatedOnly ? 'personalized' : 'all'}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setAnnotatedOnly(e.target.value === 'personalized')
        }
      >
        <Radio value="all" label={formatMessage(messages.resultsTypeAll)} />
        <Radio
          value="personalized"
          label={formatMessage(messages.resultsTypePersonalized)}
        />
      </RadioGroup>
    </Box>
  );
}

function LabSourceFilter() {
  const { extractedOnly, setExtractedOnly } = useSearchContext();
  const { formatMessage } = useIntl();

  return (
    <Box paddingTop={1}>
      <RadioGroup
        title={formatMessage(messages.labSource)}
        name="labSource"
        value={extractedOnly ? 'extracted' : 'all'}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setExtractedOnly(e.target.value === 'extracted')
        }
      >
        <Radio value="all" label={formatMessage(messages.labSourceAll)} />
        <Radio
          value="extracted"
          label={formatMessage(messages.labSourceExtracted)}
        />
      </RadioGroup>
    </Box>
  );
}
